import { animate, motion, useInView, useReducedMotion } from "framer-motion";
import { useEffect, useRef, useState } from "react";
import { Building2, Clock, Globe2 } from "lucide-react";
import { deploymentImpactStats } from "../data/impactMetrics";
import { slowFadeUp, slowStagger } from "../utils/animations";

const STAT_ICONS = [Building2, Globe2, Clock];
const STAT_VALUE_ACCENTS = [
  "text-blue-600",
  "text-emerald-600",
  "text-sky-600",
];
const STAT_ICON_TONES = [
  "bg-blue-100 text-blue-700",
  "bg-emerald-100 text-emerald-700",
  "bg-sky-100 text-sky-700",
];

function formatStatCount(latest, format) {
  const n = Math.round(latest);
  switch (format) {
    case "plus":
      return `${n}+`;
    case "millions":
      return `${n}M+`;
    case "crores":
      return `${n} Cr+`;
    case "percent":
      return `${n}%`;
    default:
      return `${n}`;
  }
}

function AnimatedStatValue({
  target,
  format,
  inView,
  staggerIndex,
  prefersReducedMotion,
  className,
}) {
  const [display, setDisplay] = useState(() =>
    prefersReducedMotion
      ? formatStatCount(target, format)
      : formatStatCount(0, format)
  );

  useEffect(() => {
    if (prefersReducedMotion) {
      setDisplay(formatStatCount(target, format));
      return;
    }
    if (!inView) {
      setDisplay(formatStatCount(0, format));
      return;
    }
    const controls = animate(0, target, {
      duration: 1.75,
      delay: staggerIndex * 0.1,
      ease: [0.22, 1, 0.36, 1],
      onUpdate: (latest) => setDisplay(formatStatCount(latest, format)),
    });
    return () => controls.stop();
  }, [inView, target, format, staggerIndex, prefersReducedMotion]);

  return <p className={className}>{display}</p>;
}

export default function DeploymentImpactStats({ className = "" }) {
  const blockRef = useRef(null);
  const inView = useInView(blockRef, { once: true, amount: 0.25 });
  const prefersReducedMotion = useReducedMotion();

  return (
    <motion.div
      ref={blockRef}
      variants={slowStagger}
      initial="hidden"
      whileInView="show"
      viewport={{ once: true, amount: 0.2 }}
      className={[
        "relative isolate overflow-hidden rounded-2xl border border-slate-200/80 bg-gradient-to-br from-white via-slate-50/70 to-blue-50/50 px-5 py-8 shadow-lg shadow-slate-900/5 md:px-8 md:py-10",
        className.trim(),
      ]
        .filter(Boolean)
        .join(" ")}
    >
      <motion.div
        className="pointer-events-none absolute -right-16 -top-20 h-56 w-56 rounded-full bg-blue-400/15 blur-3xl"
        aria-hidden
        initial={{ opacity: 0 }}
        animate={inView ? { opacity: 1 } : { opacity: 0 }}
        transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
      />
      <motion.div
        className="pointer-events-none absolute -bottom-24 -left-20 h-64 w-64 rounded-full bg-indigo-400/10 blur-3xl"
        aria-hidden
        initial={{ opacity: 0 }}
        animate={inView ? { opacity: 1 } : { opacity: 0 }}
        transition={{ duration: 0.8, delay: 0.1, ease: [0.22, 1, 0.36, 1] }}
      />
      <div
        className="pointer-events-none absolute inset-0 bg-[radial-gradient(ellipse_80%_50%_at_50%_-20%,rgba(59,130,246,0.08),transparent)]"
        aria-hidden
      />

      <p className="relative z-10 mb-6 text-center font-display text-sm font-bold uppercase tracking-wide text-slate-500">
        Real impact across deployments
      </p>
      <motion.div
        variants={slowStagger}
        className="relative z-10 grid grid-cols-1 gap-y-6 sm:grid-cols-3 sm:gap-y-0"
      >
        {deploymentImpactStats.map((stat, index) => {
          const StatIcon = STAT_ICONS[index];
          const valueAccent = STAT_VALUE_ACCENTS[index];
          const iconTone = STAT_ICON_TONES[index];
          const isLast = index === deploymentImpactStats.length - 1;

          return (
            <motion.div
              variants={slowFadeUp}
              key={stat.label}
              className={[
                "flex flex-col px-4 sm:flex-row sm:items-start sm:gap-3",
                !isLast && "border-b border-slate-200 sm:border-b-0",
                index > 0 && "sm:border-l sm:border-slate-200",
              ]
                .filter(Boolean)
                .join(" ")}
            >
              <motion.div
                className={`mx-auto inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-xl sm:mx-0 ${iconTone}`}
                initial={{ opacity: 0, scale: 0.92 }}
                animate={
                  prefersReducedMotion || inView
                    ? { opacity: 1, scale: 1 }
                    : { opacity: 0, scale: 0.92 }
                }
                transition={{
                  duration: 0.45,
                  delay: prefersReducedMotion ? 0 : index * 0.08,
                  ease: [0.22, 1, 0.36, 1],
                }}
              >
                <StatIcon className="size-5" strokeWidth={1.9} aria-hidden />
              </motion.div>
              <motion.div
                className="mt-3 min-w-0 flex-1 text-center sm:mt-0 sm:text-left"
                initial={{ opacity: 0, y: 8 }}
                animate={
                  prefersReducedMotion || inView
                    ? { opacity: 1, y: 0 }
                    : { opacity: 0, y: 8 }
                }
                transition={{
                  duration: 0.5,
                  delay: prefersReducedMotion ? 0 : 0.1 + index * 0.08,
                  ease: [0.22, 1, 0.36, 1],
                }}
              >
                <AnimatedStatValue
                  target={stat.target}
                  format={stat.valueFormat}
                  inView={inView}
                  staggerIndex={index}
                  prefersReducedMotion={prefersReducedMotion}
                  className={`font-display text-2xl font-extrabold tabular-nums md:text-3xl ${valueAccent}`}
                />
                <p className="mt-0.5 text-sm font-semibold text-secondary">
                  {stat.label}
                </p>
                <p className="mt-1 text-xs text-muted">{stat.sub}</p>
              </motion.div>
            </motion.div>
          );
        })}
      </motion.div>
    </motion.div>
  );
}
